/**
 * 멤버 표식 (DESIGN_SPEC 5장 v1.2.1, 시안 03 README 5.1).
 *
 * - **모든 크기에서 초상 사진**(`/assets/members/{key}.jpg`, cover)
 * - 상징 배지 없음(시안 05 결정 18: 모든 크기에서 초상만)
 * - 사진을 못 읽으면 대체: 멤버 색 14% 바탕 + 상징(지름×0.56). 경로는 Avatar.dc.html 그대로
 * - ring: 미니 프로필을 연 얼굴의 눌림 표시(3px --color-brand)
 * 별은 브랜드 전용이라 쓰지 않습니다.
 */
import { useState } from 'react';

const SYMBOLS = {
    // 물결
    imo: (
        <>
            <path d="M3 9c2 0 2-2 4.5-2S10 9 12 9s2-2 4.5-2S19 9 21 9" />
            <path d="M3 15c2 0 2-2 4.5-2S10 15 12 15s2-2 4.5-2S19 15 21 15" />
        </>
    ),
    // 작은 산
    oreum: <path d="M3 19l6-9 4 5 3-4 5 8z" />,
    // 바둑돌
    son: (
        <>
            <circle cx="12" cy="12" r="7" />
            <path d="M9 10a3 3 0 0 1 3-2" />
        </>
    ),
    // 향수병
    eunbyeol: (
        <>
            <rect x="7" y="9" width="10" height="11" rx="2" />
            <path d="M10 9V6h4v3" />
            <path d="M12 6V3.5" />
        </>
    ),
    // 주판
    do: (
        <>
            <rect x="4" y="4" width="16" height="16" rx="2" />
            <path d="M4 12h16" />
            <circle cx="8.5" cy="8" r="1.3" />
            <circle cx="12.5" cy="8" r="1.3" />
            <circle cx="15.5" cy="16" r="1.3" />
        </>
    ),
};

export const AVATAR_SIZES = [20, 24, 28, 32, 40, 44, 56, 64, 96, 132, 144];

export function portraitUrlOf(memberKey) {
    return `/assets/members/${memberKey}.jpg`;
}

function strokeWidthFor(size) {
    if (size >= 56) {
        return 1.5;
    }
    if (size <= 24) {
        return 2.2;
    }
    return 1.75;
}

function Symbol({ memberKey, size, strokeWidth }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={`var(--color-m-${memberKey})`}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            {SYMBOLS[memberKey]}
        </svg>
    );
}

export function Avatar({ memberKey, size = 32, ring = false, className = '' }) {
    const [photoFailed, setPhotoFailed] = useState(false);
    const known = Boolean(SYMBOLS[memberKey]);
    if (!known) {
        console.error('[avatar] 알 수 없는 멤버', memberKey);
    }
    const tint = known ? `color-mix(in srgb, var(--color-m-${memberKey}) 14%, #fffdf8)` : 'var(--color-surface-sunken)';

    return (
        <span
            aria-hidden="true"
            className={`relative inline-flex shrink-0 rounded-full ${className}`}
            style={{ width: size, height: size }}
        >
            <span
                className="flex size-full items-center justify-center overflow-hidden rounded-full"
                style={{
                    background: tint,
                    boxShadow: ring ? '0 0 0 3px var(--color-brand)' : undefined,
                }}
            >
                {known && !photoFailed ? (
                    <img
                        src={portraitUrlOf(memberKey)}
                        alt=""
                        width={size}
                        height={size}
                        loading="lazy"
                        decoding="async"
                        onError={() => setPhotoFailed(true)}
                        className="size-full object-cover"
                    />
                ) : null}
                {known && photoFailed ? (
                    <Symbol memberKey={memberKey} size={Math.round(size * 0.56)} strokeWidth={strokeWidthFor(size)} />
                ) : null}
            </span>
        </span>
    );
}

/** 상단 바의 다섯 겹침: 20px, 왼쪽으로 -6px, 1.5px --color-bg 테두리 */
export function AvatarStack({ memberKeys, size = 20 }) {
    return (
        <span aria-hidden="true" className="flex items-center">
            {memberKeys.map((memberKey, index) => (
                <span
                    key={memberKey}
                    className="rounded-full"
                    style={{ marginLeft: index === 0 ? 0 : -6, boxShadow: '0 0 0 1.5px var(--color-bg)' }}
                >
                    <Avatar memberKey={memberKey} size={size} />
                </span>
            ))}
        </span>
    );
}
